import React, { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { AgentOutputItem, AgentSessionListItem } from '../types.js'
import { outputKindLabel } from '../../utils/dashboardType'
import { PAGE_HELPERS, pageHelperTitle } from '../pageHelpers'

const DRAWER_WIDTH = 280
import SlidePanel from './SlidePanel.js'

const STORAGE_KEY = 'agent-drawer-open'

function getRelativeTime(unixSeconds: number): string {
  const now = Date.now() / 1000
  const diff = now - unixSeconds
  if (diff < 60) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 172800) return 'Yesterday'
  return `${Math.floor(diff / 86400)}d ago`
}

function getSessionTitle(session: AgentSessionListItem): string {
  if (session.title) return session.title
  return 'Untitled'
}

/**
 * The badge's word, or '' for no badge. "Working" is a turn in flight and
 * nothing else (#1430); a chat whose process is live with no turn running is
 * "Ready". A chat whose process is not running has no badge: there is nothing
 * to do about it, the next message starts it.
 */
export function getSessionStateLabel(session: Pick<AgentSessionListItem, 'container_state' | 'active_query_id'>): string {
  if (session.active_query_id) return 'Working'
  return session.container_state === 'running' ? 'Ready' : ''
}

/**
 * Whether past-chat search (`pt chats search`) can find this chat yet (#1475).
 * The conversation index takes a chat in about 10 minutes after its last
 * message; a chat that changed since is only partly searchable.
 */
function getSearchIndicator(session: AgentSessionListItem): { icon: React.ReactNode; label: string } {
  if (!session.indexed_at) {
    return {
      icon: <SearchOffIcon sx={{ fontSize: 14, color: '#9ca3af' }} />,
      label: "Past-chat search can't find this chat yet. Chats are indexed about 10 minutes after their last message.",
    }
  }
  const when = new Date(session.indexed_at * 1000).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
  const grown = session.updated_at > session.indexed_at + 60
  return {
    icon: <ManageSearchIcon sx={{ fontSize: 14, color: '#9ca3af' }} />,
    label: `Past-chat search finds this chat. Indexed ${when}.${grown ? ' Newer messages may not be searchable yet.' : ''}`,
  }
}

function getStatusColor(state: string | undefined): { bg: string; fg: string } {
  switch (state) {
    case 'working':
      // Inverted (filled) lozenge so a session with a turn in flight stands
      // out in the list — matches the toolbar's Running pill.
      return { bg: '#16a34a', fg: '#ffffff' }
    case 'running':
      // Up but idle: ready, not busy. Quiet green so it still reads as live.
      return { bg: '#ecfdf5', fg: '#15803d' }
    case 'error':
      return { bg: '#fef2f2', fg: '#dc2626' }
    case 'published':
      return { bg: '#f0e6ff', fg: '#7c3aed' }
    default:
      return { bg: '#f3f4f6', fg: '#6b7280' }
  }
}

/** The list's quiet grey lozenge, the one an unknown state gets. */
const NEUTRAL_CHIP = getStatusColor(undefined)

/** The session list itself — user filter, search, and the sessions.
 *  Shared by <ChatHistoryDrawer> (its own drawer chrome) and by the History tab
 *  of <AgentSidePanel>, so the two can never drift. */
export interface ChatHistoryContentProps {
  sessions: AgentSessionListItem[]
  activeSessionId?: string
  onSelectSession: (sessionId: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  users?: string[]
  selectedUserEmail?: string
  onUserFilterChange?: (userEmail: string) => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  /** Search runs on the host: it re-fetches the session list with the query,
   *  so `sessions` is already the matching chats. */
  onSearchSubmit?: (query: string) => void
  isSearching?: boolean
  currentUserEmail?: string
  /**
   * Manage chats (#1634). A chat's owner renames, stars and deletes it from
   * its row; the controls appear only on the caller's own chats
   * (`user_email === currentUserEmail`) and only when the host passes the
   * handler. `onToggleStar` gets the state the person asked for.
   * `onDeleteSession` resolves to a message when the server refuses (a
   * shared chat, a chat on a dashboard) and the dialog shows it; it resolves
   * to nothing when the chat is gone.
   */
  onRenameSession?: (sessionId: string, title: string) => void | Promise<void>
  onDeleteSession?: (sessionId: string) => string | void | Promise<string | void>
  onToggleStar?: (sessionId: string, starred: boolean) => void | Promise<void>
  /**
   * Turns a session's job NAME into what the reader should see. The list
   * itself only knows the code the session was created with (`s.job`), and a
   * code is never what a user recognises — Platinum maps it to the job's
   * display name. Leave it unset and the code is shown as it is.
   */
  labelJob?: (job: string) => string
  /**
   * Why the person can't rename, star or delete a chat, or undefined when they
   * can. When passed, every row shows its actions, and one the person can't
   * use is disabled with the reason beside it, never hidden. When absent, the
   * actions show on the person's own chats only.
   */
  actionReason?: (action: ChatRowAction, session: AgentSessionListItem) => string | undefined
  /**
   * Past-work search (#1649). The outputs past chats made that match the
   * search: shown as an "Outputs" group above the chats, only while a search
   * is active. `outputsNotice` is the server's sentence saying these are
   * unchecked past work; it is shown above the rows as it is.
   */
  outputs?: AgentOutputItem[]
  outputsNotice?: string
  onSelectOutput?: (output: AgentOutputItem) => void
  /**
   * The Page helpers filter. '' (the default) leaves page helper chats out of
   * the list, 'all' lists every page's, a page key lists that page's. The
   * control is drawn only when the host passes `onPageFilterChange`; the host
   * sends the value as `?page=`.
   */
  pageFilter?: string
  onPageFilterChange?: (page: string) => void
  /**
   * Host content shown above the chats, under the filters: a way back into
   * something the list itself does not hold (Platinum's unfinished guided
   * conversations, which are kept out of History). Nothing is drawn when
   * absent.
   */
  pinned?: React.ReactNode
}

export type ChatRowAction = 'rename' | 'star' | 'delete'

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const

export interface ChatHistoryDrawerProps extends ChatHistoryContentProps {
  open: boolean
  onClose: () => void
  onBrowseApps?: () => void
}

export function ChatHistoryContent({
  sessions,
  activeSessionId,
  onSelectSession,
  onLoadMore,
  hasMore,
  users,
  selectedUserEmail = 'me',
  onUserFilterChange,
  searchQuery = '',
  onSearchChange,
  onSearchSubmit,
  isSearching,
  currentUserEmail,
  onRenameSession,
  onDeleteSession,
  onToggleStar,
  labelJob,
  actionReason,
  outputs = [],
  outputsNotice,
  onSelectOutput,
  pageFilter = '',
  onPageFilterChange,
  pinned,
}: ChatHistoryContentProps) {
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The row being renamed, its draft, and why a draft was refused.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftError, setDraftError] = useState('')
  const [menu, setMenu] = useState<{ anchor: HTMLElement; sessionId: string } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AgentSessionListItem | null>(null)
  const [deleteError, setDeleteError] = useState('')
  const [deleting, setDeleting] = useState(false)

  const ownsSession = (s: AgentSessionListItem) =>
    Boolean(currentUserEmail) && s.user_email.toLowerCase() === currentUserEmail!.toLowerCase()

  const handleStartRename = (s: AgentSessionListItem) => {
    setMenu(null)
    setDraftTitle(s.title || '')
    setDraftError('')
    setEditingId(s.id)
  }

  const handleCancelRename = () => {
    setEditingId(null)
    setDraftError('')
  }

  const handleCommitRename = (s: AgentSessionListItem) => {
    const title = draftTitle.trim()
    if (!title) {
      setDraftError('A chat needs a name')
      return
    }
    setEditingId(null)
    setDraftError('')
    if (title !== s.title) void onRenameSession?.(s.id, title)
  }

  const handleStartDelete = (s: AgentSessionListItem) => {
    setMenu(null)
    setDeleteError('')
    setDeleteTarget(s)
  }

  const handleCloseDelete = () => {
    if (!deleting) setDeleteTarget(null)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !onDeleteSession) return
    setDeleting(true)
    try {
      const refusal = await onDeleteSession(deleteTarget.id)
      if (refusal) setDeleteError(refusal)
      else setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  const menuTarget = sessions.find(x => x.id === menu?.sessionId)

  // A menu action. With a reason it stays in the menu, greyed, the reason
  // under its name, and never runs.
  const renderMenuItem = (action: 'rename' | 'delete', label: string, run: (s: AgentSessionListItem) => void) => {
    const reason = menuTarget ? actionReason?.(action, menuTarget) : undefined
    return (
      <MenuItem
        disabled={Boolean(reason)}
        sx={reason ? { fontSize: 13, alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } } : { fontSize: 13 }}
        onClick={() => {
          if (reason) return
          if (menuTarget) run(menuTarget)
        }}
      >
        {reason ? (
          <ListItemText
            primary={label}
            secondary={reason}
            primaryTypographyProps={{ color: 'text.disabled', fontSize: 13 }}
            secondaryTypographyProps={{ color: 'text.secondary', fontSize: 12, sx: { whiteSpace: 'normal', maxWidth: 280 } }}
          />
        ) : label}
      </MenuItem>
    )
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSearchSubmit && searchQuery) {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      onSearchSubmit(searchQuery)
    }
  }

  const handleSearchInput = (value: string) => {
    if (onSearchChange) onSearchChange(value)
    if (onSearchSubmit) {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (value.length >= 2) {
        searchTimerRef.current = setTimeout(() => onSearchSubmit(value), 300)
      }
    }
  }

  const showUserEmail = selectedUserEmail === '*' || (selectedUserEmail !== 'me' && selectedUserEmail !== currentUserEmail)
  const showOutputs = Boolean(searchQuery.trim()) && outputs.length > 0

  return (
    <>
      {/* User filter and search controls */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        {onUserFilterChange && (
          <Select
            value={selectedUserEmail}
            onChange={(e) => onUserFilterChange(e.target.value)}
            size="small"
            sx={{ fontSize: 12, height: 30, '& .MuiSelect-select': { py: 0.5 } }}
            fullWidth
          >
            <MenuItem value="me" sx={{ fontSize: 12 }}>My sessions</MenuItem>
            <MenuItem value="*" sx={{ fontSize: 12 }}>All users</MenuItem>
            {users?.filter(u => u !== currentUserEmail).map(email => (
              <MenuItem key={email} value={email} sx={{ fontSize: 12 }}>{email}</MenuItem>
            ))}
          </Select>
        )}
        {onPageFilterChange && (
          <Select
            value={pageFilter}
            onChange={(e) => onPageFilterChange(e.target.value)}
            size="small"
            displayEmpty
            SelectDisplayProps={{ 'aria-label': 'Page helpers' }}
            sx={{ fontSize: 12, height: 30, '& .MuiSelect-select': { py: 0.5 } }}
            fullWidth
          >
            <MenuItem value="" sx={{ fontSize: 12 }}>Page helpers hidden</MenuItem>
            <MenuItem value="all" sx={{ fontSize: 12 }}>All page helpers</MenuItem>
            {PAGE_HELPERS.map(h => (
              <MenuItem key={h.key} value={h.key} sx={{ fontSize: 12 }}>{h.title}</MenuItem>
            ))}
          </Select>
        )}
        {onSearchChange && (
          <TextField
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search chats"
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 12, height: 30 },
              },
            }}
          />
        )}
      </Box>

      {pinned}

      <List dense sx={{ overflow: 'auto', flex: 1, p: 0 }}>
        {showOutputs && (
          <Box component="li" data-testid="output-results" sx={{ listStyle: 'none' }}>
            <Typography sx={{ px: 1.5, pt: 1, fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Outputs
            </Typography>
            {outputsNotice && (
              <Typography data-testid="outputs-notice" sx={{ px: 1.5, pt: 0.25, pb: 0.5, fontSize: 11, color: 'text.secondary' }}>
                {outputsNotice}
              </Typography>
            )}
            <List dense disablePadding aria-label="Outputs">
              {outputs.map(o => (
                <ListItem key={o.id} disablePadding>
                  <ListItemButton
                    onClick={() => onSelectOutput?.(o)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      '&:hover': { backgroundColor: '#f9fafb' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {o.title || o.session_title || 'Untitled'}
                        </Typography>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={outputKindLabel(o.kind)}
                              size="small"
                              sx={{
                                fontSize: 10,
                                height: 18,
                                backgroundColor: NEUTRAL_CHIP.bg,
                                color: NEUTRAL_CHIP.fg,
                                '& .MuiChip-label': { color: NEUTRAL_CHIP.fg },
                              }}
                            />
                            <Chip label="Unchecked" size="small" variant="outlined" color="warning" sx={{ fontSize: 10, height: 18 }} />
                          </Box>
                          <Typography component="span" data-testid="output-byline" sx={{ fontSize: 11, color: '#9ca3af', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[
                              o.user_email ? `by ${o.user_email}` : '',
                              getRelativeTime(o.created_at),
                              o.job ? (labelJob ? labelJob(o.job) : o.job) : '',
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Typography sx={{ px: 1.5, pt: 1.5, pb: 0.5, fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Chats
            </Typography>
          </Box>
        )}
        {sessions.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>
              {searchQuery.trim()
                ? (isSearching ? 'Searching...' : 'No chats match')
                : 'No sessions yet'}
            </Typography>
          </Box>
        )}
        {sessions.map(s => {
          const isActive = s.id === activeSessionId
          const isPublished = s.status === 'published'
          const isWorking = !isPublished && Boolean(s.active_query_id)
          const searchIndicator = getSearchIndicator(s)
          const stateLabel = isPublished ? 'Published' : getSessionStateLabel(s)
          const statusColor = isPublished ? getStatusColor('published') : getStatusColor(isWorking ? 'working' : s.container_state)
          const isOwn = ownsSession(s)
          const isStarred = (s.starred_at ?? 0) > 0
          const showActions = Boolean(actionReason) || isOwn
          // A colleague's starred chat keeps its inert "starred by its owner"
          // mark: a disabled Unstar toggle would announce a control nobody
          // but the owner has. An unstarred one shows Star, disabled with why.
          const canStar = (isOwn || (showActions && !isStarred)) && Boolean(onToggleStar)
          const canManage = showActions && Boolean(onRenameSession || onDeleteSession)
          const starReason = actionReason?.('star', s)
          const starReasonId = `chat-star-reason-${s.id}`

          if (editingId === s.id) {
            return (
              <Box key={s.id} sx={{ py: 1, px: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={draftTitle}
                  onChange={(e) => { setDraftTitle(e.target.value); if (draftError) setDraftError('') }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleCommitRename(s) }
                    else if (e.key === 'Escape') { e.preventDefault(); handleCancelRename() }
                  }}
                  onFocus={(e) => e.target.select()}
                  error={Boolean(draftError)}
                  helperText={draftError || 'Enter to save, Escape to cancel'}
                  slotProps={{
                    htmlInput: { 'aria-label': 'Chat name', maxLength: 120 },
                    input: { sx: { fontSize: 13 } },
                    formHelperText: { sx: { fontSize: 10, mx: 0 } },
                  }}
                />
              </Box>
            )
          }

          return (
            <ListItem
              key={s.id}
              disablePadding
              secondaryAction={(canStar || canManage || isStarred) ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {canStar ? (
                    <Tooltip title={starReason ?? (isStarred ? 'Unstar' : 'Star')}>
                      {/* A disabled button takes no hover, so the span carries the tooltip. */}
                      <span>
                        <IconButton
                          size="small"
                          aria-label={isStarred ? 'Unstar chat' : 'Star chat'}
                          aria-pressed={isStarred}
                          aria-describedby={starReason ? starReasonId : undefined}
                          disabled={Boolean(starReason)}
                          onClick={() => void onToggleStar!(s.id, !isStarred)}
                          sx={{ color: isStarred ? '#f59e0b' : '#9ca3af' }}
                        >
                          {isStarred ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                        {starReason && <Box component="span" id={starReasonId} sx={visuallyHidden}>{starReason}</Box>}
                      </span>
                    </Tooltip>
                  ) : isStarred ? (
                    <Box aria-label="Starred by its owner" role="img" sx={{ display: 'flex', p: '5px', color: '#f59e0b' }}>
                      <StarIcon sx={{ fontSize: 16 }} />
                    </Box>
                  ) : null}
                  {canManage && (
                    <IconButton
                      size="small"
                      aria-label="Chat actions"
                      onClick={(e) => setMenu({ anchor: e.currentTarget, sessionId: s.id })}
                      sx={{ color: '#6b7280' }}
                    >
                      <MoreVertIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ) : undefined}
              sx={{ '& .MuiListItemButton-root': { pr: (canStar ? 4 : 0) + (canManage ? 4 : 0) + (!canStar && isStarred ? 4 : 0) + 1.5 } }}
            >
              <ListItemButton
                selected={isActive}
                onClick={() => onSelectSession(s.id)}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: isActive ? '#eff6ff' : undefined,
                  '&:hover': {
                    backgroundColor: isActive ? '#dbeafe' : '#f9fafb',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 500,
                        color: '#1f2937',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getSessionTitle(s)}
                    </Typography>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {pageHelperTitle(s.profile) && <Chip
                          label={`${pageHelperTitle(s.profile)} helper`}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: 10, height: 18 }}
                        />}
                        {stateLabel && <Chip
                          label={stateLabel}
                          size="small"
                          data-working={isWorking ? 'true' : undefined}
                          sx={{
                            fontSize: 10,
                            height: 18,
                            fontWeight: isWorking ? 600 : undefined,
                            backgroundColor: statusColor.bg,
                            color: statusColor.fg,
                            '& .MuiChip-label': { color: statusColor.fg },
                          }}
                        />}
                        <Tooltip title={searchIndicator.label} arrow>
                          <Box role="img" aria-label={searchIndicator.label} sx={{ display: 'flex', alignItems: 'center' }}>
                            {searchIndicator.icon}
                          </Box>
                        </Tooltip>
                        {s.artifact_count > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <AttachFileIcon sx={{ fontSize: 12, color: '#6b7280' }} />
                            <Typography component="span" sx={{ fontSize: 10, color: '#6b7280' }}>
                              {s.artifact_count}
                            </Typography>
                          </Box>
                        )}
                        {s.workflow_id && (
                          <Typography component="span" sx={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                            {s.workflow_id}
                          </Typography>
                        )}
                        <Typography component="span" sx={{ fontSize: 11, color: '#9ca3af', ml: 'auto' }}>
                          {getRelativeTime(s.updated_at)}
                        </Typography>
                      </Box>
                      {showUserEmail && s.user_email && s.user_email !== currentUserEmail && (
                        <Typography component="span" sx={{ fontSize: 11, color: '#6366f1', display: 'block' }}>
                          {s.user_email}
                        </Typography>
                      )}
                      {s.job && (
                        <Typography component="span" data-testid="session-job" sx={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>
                          {labelJob ? labelJob(s.job) : s.job}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          )
        })}

        {hasMore && sessions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Button
              size="small"
              color="info"
              onClick={onLoadMore}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Load more
            </Button>
          </Box>
        )}
      </List>

      <Menu
        open={Boolean(menu)}
        // A disabled action keeps its place in the arrow-key order, so a
        // keyboard or screen-reader user reaches its reason.
        MenuListProps={actionReason ? { disabledItemsFocusable: true } : undefined}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        // Rename focuses its field as the menu closes; handing focus back to
        // the kebab would blur the field and cancel the rename.
        disableRestoreFocus
      >
        {onRenameSession && renderMenuItem('rename', 'Rename', handleStartRename)}
        {onDeleteSession && renderMenuItem('delete', 'Delete', handleStartDelete)}
      </Menu>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Delete this chat?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13, color: '#374151' }}>
            {deleteTarget ? getSessionTitle(deleteTarget) : ''}
          </DialogContentText>
          <DialogContentText sx={{ fontSize: 13, mt: 1 }}>
            The chat and its history are removed. If it is working now, it stops.
            Files it made stay in storage for audit.
          </DialogContentText>
          {deleteError && <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={handleCloseDelete} disabled={deleting}>
            {deleteError ? 'Close' : 'Cancel'}
          </Button>
          {!deleteError && (
            <Button color="info" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

/**
 * The fixed-width history drawer used by the agent-create page, which has no
 * side panel. The running-session chat page uses <AgentSidePanel> instead,
 * whose History tab renders the same <ChatHistoryContent>. Both sit inside
 * <SlidePanel>, so opening from the edge slides the same way on both pages
 * (Adam, 8 Sep: the persistent MUI Drawer popped into place here).
 */
export default function ChatHistoryDrawer({
  open,
  onClose,
  onBrowseApps,
  ...content
}: ChatHistoryDrawerProps) {
  return (
    <SlidePanel open={open} width={DRAWER_WIDTH} side="left">
      <Box
        data-testid="chat-history-drawer"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderRight: '1px solid rgba(0,0,0,0.06)',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
            Chat History
          </Typography>
          <Tooltip title="Close drawer">
            <IconButton size="small" aria-label="Close drawer" onClick={onClose}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ChatHistoryContent {...content} />
        </Box>
      </Box>
    </SlidePanel>
  )
}

// Merge shim: this component was extracted twice in parallel — as
// ChatHistoryContent on main (#1302, which AgentSidePanel builds on) and as
// ChatHistoryList on the agent-table-discovery branch. ChatHistoryContent is
// the real name; these aliases keep the branch's existing callers
// (frontend AgentLeftPanel and the tests) compiling. Safe to delete once those
// callers are switched over.
export const ChatHistoryList = ChatHistoryContent
export type ChatHistoryListProps = ChatHistoryContentProps

export { DRAWER_WIDTH, STORAGE_KEY }
